import React from 'react';
import { useTranslation } from 'react-i18next';
import { useUser } from '@clerk/clerk-react';
import CgDialog from 'modules/Common/ui/CgDialog';
import { useSupplierFactorHistory } from 'modules/FreeSupplier/useSupplierFactors';
import { SupplierFactorPublicationState } from 'modules/FreeSupplier/supplierFactorContracts';
import { inclusiveEndDateFromExclusive } from 'modules/FreeSupplier/supplierFactorInterval';
import PropagationStatusBadge from './PropagationStatusBadge';
import { SupplierHistorySkeleton } from './SupplierLoadingStates';

// The supplier emissions-versioning timeline (a legitimate new-build surface,
// D48). Read-only: one row per SupplierFactorRevision in the series, newest
// first, showing current vs superseded vs withdrawn, the revision number, actor,
// emissions/SEFA of that revision, and how far it propagated.

const fmtDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  }).format(d);
};

const fmtInterval = (interval) => {
  if (!interval) return '';
  const from = (interval.startInclusive || '').slice(0, 10);
  const to = inclusiveEndDateFromExclusive(interval.endExclusive);
  return from && to ? `${from} → ${to}` : from || to;
};

// Q6 (S7 fix batch 3): a bare Clerk user id (`user_3Gcn…`) reads as noise to
// the supplier — most revisions were actored by the signed-in user
// themselves. "You" for a self-match, otherwise a short, still-distinguishing
// truncation with the full id always available via `title` (no backend
// change — Clerk's `useUser` is the only new input).
// Module-scope helpers (not exported/reused elsewhere) — take `t` as their
// first argument, explicit-namespaced keys, called from RevisionRow below.
const actorLabel = (t, actorUserId, currentUserId) => {
  if (!actorUserId)
    return { text: t('freeSupplier:revisionHistory.unknownActor', 'unknown'), title: undefined };
  if (currentUserId && actorUserId === currentUserId)
    return { text: t('freeSupplier:revisionHistory.actorYou', 'You'), title: undefined };
  const text = actorUserId.length > 14 ? `${actorUserId.slice(0, 12)}…` : actorUserId;
  return { text, title: actorUserId };
};

const revisionStatus = (t, revision) => {
  if (revision.publicationState === SupplierFactorPublicationState.Withdrawn) {
    return {
      label: t('freeSupplier:revisionHistory.statusWithdrawn', 'Withdrawn'),
      tone: 'text-red-700 bg-red-50',
      dot: 'bg-red-400',
    };
  }
  if (revision.isCurrent) {
    return {
      label: t('freeSupplier:revisionHistory.statusCurrent', 'Current'),
      tone: 'text-emerald-700 bg-emerald-50',
      dot: 'bg-emerald-500',
    };
  }
  return {
    label: t('freeSupplier:revisionHistory.statusSuperseded', 'Superseded'),
    tone: 'text-slate-500 bg-slate-100',
    dot: 'bg-slate-300',
  };
};

function RevisionRow({ revision, propagation, isLast, currentUserId }) {
  const { t } = useTranslation('freeSupplier');
  const status = revisionStatus(t, revision);
  const sefa = revision.authoritativeSefa || {};
  const actor = actorLabel(t, revision.actor?.userId, currentUserId);
  return (
    <li className="relative flex gap-3 pb-5">
      {/* Timeline rail */}
      <div className="flex flex-col items-center">
        <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${status.dot}`} />
        {!isLast ? <span className="w-px flex-1 bg-slate-200 mt-1" /> : null}
      </div>

      <div className="flex-1 min-w-0 -mt-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-bold text-slate-700">
            {t('revisionHistory.revisionNumber', 'Revision {{number}}', {
              number: revision.revisionNumber,
            })}
          </span>
          <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${status.tone}`}>
            {status.label}
          </span>
          <PropagationStatusBadge propagation={propagation} />
        </div>

        <div className="mt-1.5 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-[11px] text-slate-500">
          <span>
            {t('revisionHistory.directLabel', 'Direct')}{' '}
            <span className="font-semibold text-slate-700">{revision.emissions?.direct}</span>
          </span>
          <span>
            {t('revisionHistory.indirectLabel', 'Indirect')}{' '}
            <span className="font-semibold text-slate-700">{revision.emissions?.indirect}</span>
          </span>
          <span>
            {t('revisionHistory.totalLabel', 'Total')}{' '}
            <span className="font-semibold text-slate-700">{revision.emissions?.total}</span>
          </span>
          <span>
            {t('revisionHistory.sefaLabel', 'SEFA')}{' '}
            <span className="font-semibold text-slate-700">{sefa.total}</span>
          </span>
        </div>

        <p className="mt-1 text-[11px] text-slate-400 m-0">
          {fmtInterval(revision.interval)} · <span title={actor.title}>{actor.text}</span>
          {' · '}
          {revision.publicationState === SupplierFactorPublicationState.Withdrawn &&
          revision.withdrawnAt
            ? t('revisionHistory.withdrawnOn', 'withdrawn {{date}}', {
                date: fmtDate(revision.withdrawnAt),
              })
            : fmtDate(revision.updatedAt)}
        </p>
      </div>
    </li>
  );
}

export default function RevisionHistoryModal({
  open,
  onOpenChange,
  historyAnchorRevisionId,
  seriesLabel,
}) {
  const { t } = useTranslation('freeSupplier');
  const { data, isPending, isError, refetch } = useSupplierFactorHistory(historyAnchorRevisionId, {
    enabled: open,
  });
  const { user } = useUser();
  const revisions = data?.revisions || [];
  const propagationByRevisionId = data?.propagationByRevisionId || {};

  return (
    <CgDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('revisionHistory.title', 'Revision history')}
      description={
        seriesLabel ||
        t(
          'revisionHistory.defaultDescription',
          'Every published, superseded and withdrawn revision of this factor.',
        )
      }
      width={640}
    >
      {isPending ? (
        <SupplierHistorySkeleton rows={3} />
      ) : isError ? (
        <div className="py-8 text-center">
          <p className="text-sm text-red-700 m-0">
            {t('revisionHistory.loadError', 'We couldn’t load this factor’s history.')}
          </p>
          <button
            type="button"
            onClick={() => refetch()}
            className="mt-3 text-xs font-semibold text-primaryColor bg-primaryColor/10 rounded-xl px-3 py-1.5 border-0"
          >
            {t('revisionHistory.tryAgain', 'Try again')}
          </button>
        </div>
      ) : revisions.length === 0 ? (
        <div className="py-10 text-center text-sm text-slate-400">
          {t('revisionHistory.noRevisionsFound', 'No revisions found.')}
        </div>
      ) : (
        <ul className="list-none p-0 m-0">
          {revisions.map((revision, idx) => (
            <RevisionRow
              key={revision.id}
              revision={revision}
              propagation={propagationByRevisionId[revision.id]}
              isLast={idx === revisions.length - 1}
              currentUserId={user?.id}
            />
          ))}
        </ul>
      )}
    </CgDialog>
  );
}
